/**
 * Semantic Design Tokens - Meaning-based tokens that reference primitive tokens
 * These provide context and purpose to the design system
 */

import { PrimitiveTokens } from './primitive';
import { primitiveTokens } from './primitive';

export interface SemanticColorTokens {
  // Text colors
  text: {
    primary: string;    // Primary text color
    secondary: string;  // Secondary/muted text
    muted: string;      // Very muted text
    inverse: string;    // Inverse color
    disabled: string;   // Disabled text
  };
  
  // Background colors
  background: {
    primary: string;      // Main background
    secondary: string;    // Secondary background
    tertiary: string;     // Third level background
    card: string;         // Card/surface background
    cardHover: string;    // Card hover state
    overlay: string;      // Modal/overlay background
    inverse: string;      // Inverse background

    // Layered backgrounds for depth
    elevated: string;           // Elevated elements
    surface: string;            // Surface elements
    surfaceHover: string;       // Surface hover state
    accent: string;             // Accent background
    hover: string;              // Hover background
  };
  
  // Border colors
  border: {
    default: string;      // Default border
    light: string;        // Light border
    heavy: string;        // Heavy/prominent border
    focus: string;        // Focus state border
    error: string;        // Error state border
    success: string;      // Success state border
    warning: string;      // Warning state border
  };
  
  // Interactive/action colors
  interactive: {
    primary: string;          // Primary action color
    primaryHover: string;     // Primary action hover
    primaryActive: string;    // Primary action active
    secondary: string;        // Secondary action color
    secondaryHover: string;   // Secondary action hover
    secondaryActive: string;  // Secondary action active
    accent: string;           // Accent color
    accentHover: string;      // Accent hover
    accentActive: string;     // Accent active
  };
  
  // Feedback colors (semantic meaning)
  feedback: {
    success: string;        // Success state
    successHover: string;   // Success hover
    warning: string;        // Warning state
    warningHover: string;   // Warning hover
    error: string;          // Error state
    errorHover: string;     // Error hover
    'error-hover': string;  // Alternative naming for consistency
    info: string;           // Information state
    infoHover: string;      // Information hover
  };
  
  // RPG-specific semantic colors
  rpg: {
    hp: string;           // Hit points
    hpHover: string;      // Hit points hover
    stress: string;       // Stress
    stressHover: string;  // Stress hover
    armor: string;        // Armor
    armorHover: string;   // Armor hover
    hope: string;         // Hope
    hopeHover: string;    // Hope hover
    focus: string;        // Focus
    focusHover: string;   // Focus hover

    // Priority system colors (from biography)
    priority: {
      high: string;       // High priority accent 
      medium: string;     // Medium priority accent 
      low: string;        // Low priority accent 
    };

    // Class-specific colors (from biography)
    classes: {
      warrior: string;    
      brawler: string;    
      guardian: string;   
      ranger: string;     
      rogue: string;      
      seraph: string;     
      wizard: string;    
      sorcerer: string;   
      bard: string;       
      druid: string;
    };
  };

  // Status colors (additional semantic meanings)
  status: {
    success: string;
    warning: string;
    error: string;
    info: string;
  };
}

export interface SemanticGradientTokens {
  // Icon gradients 
  icons: {
    identity: string;     
    appearance: string;   
    background: string;   
    connections: string;  
    items: string;        
    custom: string;       
    journal: string;      
  };

  // Background gradients
  backgrounds: {
    primary: string;      // Main gradient background
    secondary: string;    // Secondary gradient
    card: string;         // Card gradient
    elevated: string;     // Elevated element gradient
  };

  // Interactive gradients
  interactive: {
    primary: string;      // Primary action gradient
    secondary: string;    // Secondary action gradient
    accent: string;       // Accent gradient
    hover: string;        // Hover state gradient
  };
}

export interface SemanticShadowTokens {
  // Enhanced shadows
  base: string;         // Base shadow
  hover: string;        // Hover shadow
  elevated: string;     // Elevated shadow
  interactive: string;  // Interactive shadow
  focus: string;        // Focus shadow
  // Elevation system shadows
  low: string;          // Low elevation
  medium: string;       // Medium elevation
  high: string;         // High elevation
  textShadow: string;   // Text shadow for depth
}

export interface SemanticBorderTokens {
  // Border radius semantic tokens
  radius: {
    none: string;     // 0
    small: string;    // Small radius
    default: string;  // Default radius
    large: string;    // Large radius
    full: string;     // Fully rounded
  };
  // Border width semantic tokens
  width: {
    none: string;     // 0
    thin: string;     // Thin border
    default: string;  // Default border
    thick: string;    // Thick border
  };
}

export interface SemanticSpacingTokens {
  // Component spacing (internal spacing)
  component: {
    '2xs': string; // Double extra small component spacing (2px)
    xs: string;   // Extra small component spacing (4px)
    sm: string;   // Small component spacing (8px)
    md: string;   // Medium component spacing (16px)
    lg: string;   // Large component spacing (24px)
    xl: string;   // Extra large component spacing (32px)
    '2xl': string; // Double extra large component spacing (48px)
  };

  // Layout spacing (between components)
  layout: {
    xs: string;   // Extra small layout spacing
    sm: string;   // Small layout spacing
    md: string;   // Medium layout spacing
    lg: string;   // Large layout spacing
    xl: string;   // Extra large layout spacing
    '2xl': string; // Double extra large layout spacing
  };

  // Interactive spacing (touch targets, etc.)
  interactive: {
    touchTarget: string;  // Minimum touch target size
    inputHeight: string;  // Standard input height
    buttonHeight: string; // Standard button height
  };

  // Viewport spacing (for scrollable content areas and lists)
  viewport: {
    contentHeight: string;     // Standard content area height
    detailsHeight: string;     // Details panel height
    listHeight: string;        // List container height
    modalHeight: string;       // Modal content height
    sidebarHeight: string;     // Full sidebar height
    sidebarMinWidth: string;   // Sidebar minimum width
    sidebarMaxWidth: string;   // Sidebar standard width
    sidebarLargeWidth: string; // Sidebar large width
    sidebarXLargeWidth: string; // Sidebar extra large width
    cardMinWidthSm: string;    // Card minimum width small (200px)
    cardMinWidthMd: string;    // Card minimum width medium (250px)
    cardMinWidthLg: string;    // Card minimum width large (300px)
    cardMinWidthXl: string;    // Card minimum width extra large (400px)
    inputCompactNumeric: string; // Compact numeric input width (bonus: +XX)
    inputCompactField: string;   // Minimum field width when space-constrained
    inputMediumMin: string;      // Medium input/field minimum width
    inputStandardMin: string;    // Standard minimum for text inputs
  };

  // Viewport breakpoints (for media queries)
  breakpoints: {
    sm: string;    // Small screen breakpoint (640px)
    md: string;    // Medium screen breakpoint (768px)
    lg: string;    // Large screen breakpoint (1024px)
    xl: string;    // Extra large screen breakpoint (1280px)
    '2xl': string; // Double extra large screen breakpoint (1536px)
  };
}

export interface SemanticSizingTokens {
  // Icon sizes
  icon: {
    small: string;    // Small icon size
    medium: string;   // Medium icon size
    large: string;    // Large icon size
    xlarge: string;   // Extra large icon size
  };

  // Target sizes for interactive elements
  target: {
    minimum: string;  // Minimum touch target
  };
}

export interface SemanticTypographyTokens {
  // Heading scale
  headings: {
    h1: {
      fontSize: string;
      fontWeight: number;
      lineHeight: number;
      letterSpacing: string;
    };
    h2: {
      fontSize: string;
      fontWeight: number;
      lineHeight: number;
      letterSpacing: string;
    };
    h3: {
      fontSize: string;
      fontWeight: number;
      lineHeight: number;
      letterSpacing: string;
    };
    h4: {
      fontSize: string;
      fontWeight: number;
      lineHeight: number;
      letterSpacing: string;
    };
    h5: {
      fontSize: string;
      fontWeight: number;
      lineHeight: number;
      letterSpacing: string;
    };
    h6: {
      fontSize: string;
      fontWeight: number;
      lineHeight: number;
      letterSpacing: string;
    };
  };
  
  // Body text
  body: {
    large: {
      fontSize: string;
      fontWeight: number;
      lineHeight: number;
    };
    base: {
      fontSize: string;
      fontWeight: number;
      lineHeight: number;
    };
    small: {
      fontSize: string;
      fontWeight: number;
      lineHeight: number;
    };
  };
  
  // Special text types
  caption: {
    fontSize: string;
    fontWeight: number;
    lineHeight: number;
    letterSpacing: string;
  };
  
  label: {
    fontSize: string;
    fontWeight: number;
    lineHeight: number;
    letterSpacing: string;
  };
  
  code: {
    fontFamily: string[];
    fontSize: string;
    fontWeight: number;
    lineHeight: number;
  };
}

export interface SemanticAnimationTokens {
  // Enhanced animations (from biography system)
  transitions: {
    base: string;           // Base transition for most elements
    slow: string;           // Slower transition for emphasis
    fast: string;           // Fast transition for responsiveness
  };
  transforms: {
    hoverLift: string;      // Subtle lift on hover (-1px to -2px)
    cardHover: string;      // Card hover transform
    iconHover: string;      // Icon hover transform (scale)
  };
  timing: {
    ease: string;           // Smooth easing function
    bouncy: string;         // Slightly bouncy for playfulness
  };
}

export interface SemanticMotionTokens {
  // Motion duration tokens
  duration: {
    fast: string;     // Fast animation duration
    medium: string;   // Medium animation duration
    slow: string;     // Slow animation duration
  };

  // Motion easing tokens
  easing: {
    standard: string; // Standard easing function
    ease: string;     // Alternative easing function
  };
}

export interface SemanticBreakpointTokens {
  mobile: string;    // Mobile breakpoint
  tablet: string;    // Tablet breakpoint
  desktop: string;   // Desktop breakpoint
  wide: string;      // Wide desktop breakpoint
}

// Main semantic tokens interface
export interface SemanticTokens {
  colors: SemanticColorTokens;
  gradients: SemanticGradientTokens;
  shadows: SemanticShadowTokens;
  borders: SemanticBorderTokens;
  spacing: SemanticSpacingTokens;
  sizing: SemanticSizingTokens;
  typography: SemanticTypographyTokens;
  animations: SemanticAnimationTokens;
  motion: SemanticMotionTokens;
  breakpoints: SemanticBreakpointTokens;
}

// Helper type for referencing primitive tokens in semantic tokens
export type TokenReference<T = string> = T | ((primitives: PrimitiveTokens) => T);

// =============================================================================
// THEME-AGNOSTIC SEMANTIC TOKEN IMPLEMENTATIONS
// These provide semantic meaning and structure without theme-specific colors
// Theme-specific implementations are in individual theme files
// =============================================================================

// Theme-agnostic semantic spacing tokens (same across all themes)
export const semanticSpacing: SemanticSpacingTokens = {
  // Component spacing (internal spacing within components)
  component: {
    '2xs': '2px',                         // 2px - for borders, fine details
    xs: primitiveTokens.spacing[1],       // 4px - --spacing-xs: 0.25rem
    sm: primitiveTokens.spacing[2],       // 8px - --spacing-sm: 0.5rem
    md: primitiveTokens.spacing[4],       // 16px - --spacing-md: 1rem
    lg: primitiveTokens.spacing[6],       // 24px - --spacing-lg: 1.5rem
    xl: primitiveTokens.spacing[8],       // 32px - --spacing-xl: 2rem
    '2xl': primitiveTokens.spacing[12],   // 48px - --spacing-2xl: 3rem
  },

  // Layout spacing (between components and sections)
  layout: {
    xs: primitiveTokens.spacing[2],       // 8px
    sm: primitiveTokens.spacing[4],       // 16px
    md: primitiveTokens.spacing[6],       // 24px
    lg: primitiveTokens.spacing[8],       // 32px
    xl: primitiveTokens.spacing[12],      // 48px - --spacing-2xl: 3rem
    '2xl': primitiveTokens.spacing[16],   // 64px
  },

  // Interactive spacing (touch targets and input dimensions)
  interactive: {
    touchTarget: primitiveTokens.spacing[11],    // 44px - --touch-target-min: 44px
    inputHeight: primitiveTokens.spacing[12],    // 48px - --input-height: 48px
    buttonHeight: primitiveTokens.spacing[11],   // 44px - --button-height: 44px
  },

  // Viewport spacing (for scrollable content areas and lists)
  viewport: {
    contentHeight: '60vh',        // Standard content area height
    detailsHeight: '70vh',        // Details panel height
    listHeight: '50vh',           // List container height
    modalHeight: '80vh',          // Modal content height
    sidebarHeight: '100vh',       // Full sidebar height
    sidebarMinWidth: '16rem',     // Sidebar minimum width (256px)
    sidebarMaxWidth: '20rem',     // Sidebar standard width (320px)
    sidebarLargeWidth: '24rem',   // Sidebar large width (384px)
    sidebarXLargeWidth: '28rem',  // Sidebar extra large width (448px)
    cardMinWidthSm: '200px',      // Card minimum width small
    cardMinWidthMd: '250px',      // Card minimum width medium
    cardMinWidthLg: '300px',      // Card minimum width large
    cardMinWidthXl: '400px',      // Card minimum width extra large
    inputCompactNumeric: '70px',  // Compact numeric input width (bonus: +XX)
    inputCompactField: '80px',    // Minimum field width when space-constrained
    inputMediumMin: '120px',      // Medium input/field minimum width
    inputStandardMin: '180px',    // Standard minimum for text inputs
  },

  // Viewport breakpoints (for media queries)
  breakpoints: {
    sm: '640px',    // Small screen breakpoint
    md: '768px',    // Medium screen breakpoint
    lg: '1024px',   // Large screen breakpoint
    xl: '1280px',   // Extra large screen breakpoint
    '2xl': '1536px', // Double extra large screen breakpoint
  },
};

// Theme-agnostic semantic sizing tokens (same across all themes)
export const semanticSizing: SemanticSizingTokens = {
  // Icon sizes
  icon: {
    small: primitiveTokens.spacing[4],    // 16px
    medium: primitiveTokens.spacing[6],   // 24px
    large: primitiveTokens.spacing[8],    // 32px
    xlarge: primitiveTokens.spacing[10],  // 40px
  },

  // Target sizes for interactive elements
  target: {
    minimum: primitiveTokens.spacing[11], // 44px - minimum touch target
  },
};

// Theme-agnostic semantic typography tokens (same across all themes)
export const semanticTypography: SemanticTypographyTokens = {
  // Heading scale
  headings: {
    h1: {
      fontSize: primitiveTokens.typography.fontSizes['2xl'],     // --primitive-typography-font-sizes-2xl: 2rem
      fontWeight: primitiveTokens.typography.fontWeights.bold,   // --primitive-typography-font-weights-bold: 700
      lineHeight: primitiveTokens.typography.lineHeights.tight, // --line-height-tight: 1.2
      letterSpacing: primitiveTokens.typography.letterSpacing.normal,
    },
    h2: {
      fontSize: primitiveTokens.typography.fontSizes.xl,        // --primitive-typography-font-sizes-xl: 1.5rem
      fontWeight: primitiveTokens.typography.fontWeights.bold,  // --primitive-typography-font-weights-bold: 700
      lineHeight: primitiveTokens.typography.lineHeights.tight, // --line-height-tight: 1.2
      letterSpacing: primitiveTokens.typography.letterSpacing.normal,
    },
    h3: {
      fontSize: primitiveTokens.typography.fontSizes.lg,        // --primitive-typography-font-sizes-lg: 1.2rem
      fontWeight: primitiveTokens.typography.fontWeights.semibold, // --primitive-typography-font-weights-semibold: 600
      lineHeight: primitiveTokens.typography.lineHeights.tight, // --line-height-tight: 1.2
      letterSpacing: primitiveTokens.typography.letterSpacing.normal,
    },
    h4: {
      fontSize: primitiveTokens.typography.fontSizes.base,      // --primitive-typography-font-sizes-base: 0.9rem
      fontWeight: primitiveTokens.typography.fontWeights.semibold, // --primitive-typography-font-weights-semibold: 600
      lineHeight: primitiveTokens.typography.lineHeights.normal, // --line-height-normal: 1.4
      letterSpacing: primitiveTokens.typography.letterSpacing.normal,
    },
    h5: {
      fontSize: primitiveTokens.typography.fontSizes.sm,        // --primitive-typography-font-sizes-sm: 0.8rem
      fontWeight: primitiveTokens.typography.fontWeights.semibold, // --primitive-typography-font-weights-semibold: 600
      lineHeight: primitiveTokens.typography.lineHeights.normal, // --line-height-normal: 1.4
      letterSpacing: primitiveTokens.typography.letterSpacing.normal,
    },
    h6: {
      fontSize: primitiveTokens.typography.fontSizes.xs,        // --primitive-typography-font-sizes-xs: 0.7rem
      fontWeight: primitiveTokens.typography.fontWeights.semibold, // --primitive-typography-font-weights-semibold: 600
      lineHeight: primitiveTokens.typography.lineHeights.normal, // --line-height-normal: 1.4
      letterSpacing: primitiveTokens.typography.letterSpacing.wide,
    },
  },
  
  // Body text scales
  body: {
    large: {
      fontSize: primitiveTokens.typography.fontSizes.lg,       // --primitive-typography-font-sizes-lg: 1.2rem
      fontWeight: primitiveTokens.typography.fontWeights.normal, // --primitive-typography-font-weights-normal: 400
      lineHeight: primitiveTokens.typography.lineHeights.relaxed, // --line-height-relaxed: 1.6
    },
    base: {
      fontSize: primitiveTokens.typography.fontSizes.base,     // --primitive-typography-font-sizes-base: 0.9rem
      fontWeight: primitiveTokens.typography.fontWeights.normal, // --primitive-typography-font-weights-normal: 400
      lineHeight: primitiveTokens.typography.lineHeights.normal, // --line-height-normal: 1.4
    },
    small: {
      fontSize: primitiveTokens.typography.fontSizes.sm,       // --primitive-typography-font-sizes-sm: 0.8rem
      fontWeight: primitiveTokens.typography.fontWeights.normal, // --primitive-typography-font-weights-normal: 400
      lineHeight: primitiveTokens.typography.lineHeights.normal, // --line-height-normal: 1.4
    },
  },
  
  // Special text types
  caption: {
    fontSize: primitiveTokens.typography.fontSizes.xs,         // --primitive-typography-font-sizes-xs: 0.7rem
    fontWeight: primitiveTokens.typography.fontWeights.normal, // --primitive-typography-font-weights-normal: 400
    lineHeight: primitiveTokens.typography.lineHeights.normal, // --line-height-normal: 1.4
    letterSpacing: primitiveTokens.typography.letterSpacing.normal,
  },
  
  label: {
    fontSize: primitiveTokens.typography.fontSizes.sm,         // --primitive-typography-font-sizes-sm: 0.8rem
    fontWeight: primitiveTokens.typography.fontWeights.medium, // --primitive-typography-font-weights-medium: 500
    lineHeight: primitiveTokens.typography.lineHeights.normal, // --line-height-normal: 1.4
    letterSpacing: primitiveTokens.typography.letterSpacing.normal,
  },
  
  code: {
    fontFamily: primitiveTokens.typography.fontFamilies.mono,  // Monospace font family
    fontSize: primitiveTokens.typography.fontSizes.sm,         // --primitive-typography-font-sizes-sm: 0.8rem
    fontWeight: primitiveTokens.typography.fontWeights.normal, // --primitive-typography-font-weights-normal: 400
    lineHeight: primitiveTokens.typography.lineHeights.normal, // --line-height-normal: 1.4
  },
};

// Theme-agnostic semantic animation tokens (same across all themes)
export const semanticAnimations: SemanticAnimationTokens = {
  // Enhanced transitions for vibrancy
  transitions: {
    base: 'all 0.2s ease',              // Base transition matching biography
    slow: 'all 0.3s ease',              // Slower for emphasis
    fast: 'all 0.15s ease',             // Fast for responsiveness
  },
  transforms: {
    hoverLift: 'translateY(-1px)',       // Subtle lift on hover
    cardHover: 'translateY(-2px)',       // Card hover transform from biography
    iconHover: 'scale(1.05)',            // Icon hover scale from biography
  },
  timing: {
    ease: 'cubic-bezier(0.4, 0, 0.2, 1)',     // Smooth easing
    bouncy: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',  // Slightly bouncy
  },
};

// Theme-agnostic semantic motion tokens (same across all themes)
export const semanticMotion: SemanticMotionTokens = {
  // Motion duration tokens
  duration: {
    fast: '0.15s',     // Fast animation duration
    medium: '0.2s',    // Medium animation duration
    slow: '0.3s',      // Slow animation duration
  },

  // Motion easing tokens
  easing: {
    standard: 'cubic-bezier(0.4, 0, 0.2, 1)',  // Standard easing function
    ease: 'ease',                               // Alternative easing function
  },
};

// Theme-agnostic semantic breakpoint tokens (same across all themes)
export const semanticBreakpoints: SemanticBreakpointTokens = {
  mobile: '0px',                     
  tablet: '640px',               
  desktop: '1024px',                 
  wide: '1280px',                    
};

// Theme-agnostic semantic border tokens (same across all themes)
export const semanticBorders: SemanticBorderTokens = {
  radius: {
    none: primitiveTokens.borders.radius.none,      // 0
    small: primitiveTokens.borders.radius.sm,       // 4px
    default: primitiveTokens.borders.radius.md,     // 8px
    large: primitiveTokens.borders.radius.lg,       // 12px
    full: primitiveTokens.borders.radius.full,      // 9999px
  },
  width: {
    none: primitiveTokens.borders.width[0],         // 0
    thin: primitiveTokens.borders.width.base,       // 1px
    default: primitiveTokens.borders.width[2],      // 2px
    thick: primitiveTokens.borders.width[4],        // 4px
  },
};

// NOTE: Main semantic tokens export removed - individual themes now implement complete semantic tokens
// Colors, gradients, and shadows are theme-specific and implemented in theme files
// Only theme-agnostic tokens are exported from this file
